import classNames from "classnames";
import type { FC } from "react";
import { useState } from "react";
import { PhoneIcon, WebsiteIcon } from "../../../icons/icons";
import type { Pp2ViewModel } from "../pp2ViewModel";
import { Pp2Icon } from "../shared/pp2Icon";
import { naturalRatio, ratioStyle } from "./imageRatio";
import type { HoursStatus, QuickContact } from "./pp2HeaderModel";
import { buildQuickContacts, nameBlockOffset } from "./pp2HeaderModel";
import { Pp2Hero } from "./pp2Hero";
import styles from "./pp2Header.module.scss";

/**
 * Hero, logo, name block (h1 + business type) and the quick-contacts row (spec 3.3, H1–H4, Q1–Q4).
 * Rendered inside the shell's `<header>`; the shell owns the dashed divider below it, so nothing
 * here has a bottom margin. Nothing is rendered for unset data (R3).
 *
 * The shell passes the hero and logo URLs it still shows (`useFailedImages`): one that fails to
 * load is reported through `onImageError` and is dropped while the page is shown, as if unset, so
 * there is no broken-image box or gap and the layout follows the rules for its absence.
 */
export type Pp2HeaderProps = {
  vm: Pp2ViewModel;
  /** `vm.heroUrl` unless it failed to load */
  heroUrl: string | null;
  /** `vm.logoUrl` unless it failed to load */
  logoUrl: string | null;
  onImageError: (url: string) => void;
};

const ICON_SIZE = "20";

const HoursStatusText: FC<{ status: HoursStatus }> = ({ status }) => (
  <span data-test-id="pp2-hours-status">
    <span className={classNames(styles.statusLabel, status.isOpen && styles.open)}>{status.label}</span>
    {status.line && ` · ${status.line}`}
  </span>
);

/**
 * The library has no phone icon, and its website glyph has a hard-coded stroke colour, so both are
 * local icons drawn in `currentColor` (green), hidden like `Pp2Icon` (spec 2.4).
 */
const LocalIcon: FC<{ kind: "phone" | "website" }> = ({ kind }) => {
  const Glyph = kind === "phone" ? PhoneIcon : WebsiteIcon;
  return (
    <span aria-hidden="true" className={styles.localIcon}>
      <Glyph width={ICON_SIZE} height={ICON_SIZE} color="currentColor" />
    </span>
  );
};

const QuickContactItem: FC<{ item: QuickContact }> = ({ item }) => {
  if (item.kind === "hours") {
    return (
      <li className={styles.quickItem}>
        <Pp2Icon type="systemClock" />
        <HoursStatusText status={item.status} />
      </li>
    );
  }
  const external = item.kind === "website" ? { target: "_blank", rel: "noopener noreferrer" } : {};
  return (
    <li className={styles.quickItem}>
      {item.kind === "email" ? <Pp2Icon type="actionEmail" /> : <LocalIcon kind={item.kind} />}
      <a className={styles.link} href={item.link.href} {...external}>
        {item.link.label}
      </a>
    </li>
  );
};

/** The logo in a white box shaped like the image once it has loaded (3.3, owner feedback). */
const Pp2Logo: FC<{ url: string; alt: string; onError: () => void }> = ({ url, alt, onError }) => {
  const [ratio, setRatio] = useState<number | null>(null);
  return (
    <img
      className={styles.logo}
      style={ratioStyle("--logo-ratio", ratio)}
      src={url}
      alt={alt}
      data-test-id="pp2-logo"
      onLoad={(event) => {
        setRatio(naturalRatio(event.currentTarget));
      }}
      onError={onError}
    />
  );
};

export const Pp2Header: FC<Pp2HeaderProps> = ({ vm, heroUrl, logoUrl, onImageError }) => {
  const quickContacts = buildQuickContacts(vm);
  const offset = nameBlockOffset({ heroUrl, logoUrl });
  return (
    <div className={classNames(styles.header, heroUrl && styles.hasHero)}>
      {heroUrl && (
        <Pp2Hero
          // A new image starts over: its box is sized again after it loads
          key={heroUrl}
          url={heroUrl}
          onError={() => {
            onImageError(heroUrl);
          }}
        />
      )}
      {logoUrl && (
        <Pp2Logo
          // A new image starts over: its box is square until it loads
          key={logoUrl}
          url={logoUrl}
          alt={`${vm.displayName} logo`}
          onError={() => {
            onImageError(logoUrl);
          }}
        />
      )}
      <div
        className={classNames(
          styles.nameBlock,
          offset === "afterLogo" && styles.afterLogo,
          offset === "afterHero" && styles.afterHero,
        )}
        data-test-id="pp2-name-block"
      >
        <h1 className={styles.name}>{vm.displayName}</h1>
        {vm.businessType && <p className={styles.type}>{vm.businessType}</p>}
      </div>
      {quickContacts.length > 0 && (
        <ul className={styles.quickRow} aria-label="Contact details" data-test-id="pp2-quick-contacts">
          {quickContacts.map((item) => (
            <QuickContactItem key={item.kind} item={item} />
          ))}
        </ul>
      )}
    </div>
  );
};
